import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  User, Flame, ClipboardCheck, Image as ImageIcon, FileText, FileCheck, History as HistoryIcon, ChevronRight, Edit3, Loader2, GitBranch, MessageCircle, MessageSquare, ListTodo, Sun, Trash2, UserCheck, X, Layers
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { ProjectStatusTracker } from "@/components/ProjectStatusTracker";
import { SentDocumentsPanel } from "@/components/SentDocumentsPanel";
import { MessageThread } from "@/components/MessageThread";
import { isDemoProject, TrainingBadge } from "@/lib/demoProjects";
import CustomerSection from "./project_parts/CustomerSection";
import ConsumptionSection from "./project_parts/ConsumptionSection";
import TechnicalCheckTabs from "./project_parts/TechnicalCheckTabs";
import MediaSection from "./project_parts/MediaSection";
import OfferSection from "./project_parts/OfferSection";
import OffersListSection from "./project_parts/OffersListSection";
import SignedDocumentsBanner from "./project_parts/SignedDocumentsBanner";
import StageOverrideButton from "./project_parts/StageOverrideButton";
import HistorySection from "./project_parts/HistorySection";
import TelesalesDataCheck from "@/components/TelesalesDataCheck";
import ProjectTasksSection from "./project_parts/ProjectTasksSection";
import NotesSection from "./project_parts/NotesSection";
import ProjectDocumentsCockpit from "@/components/ProjectDocumentsCockpit";
import { useReloadBus } from "@/lib/reloadBus";

const STATUS_OPTIONS = ["Erstkontakt","Termin geplant","Vor-Ort-Aufnahme","Technische Prüfung","Angebot erstellt","Angebot versendet","Nachfassen","Gewonnen","Verloren","Archiviert"];

const ALL_TABS = [
  { id: "messages", label: "Nachrichten", icon: MessageCircle },
  { id: "customer", label: "Kunde", icon: User },
  { id: "consumption", label: "Verbrauch", icon: Flame, hideForInstaller: true },
  { id: "offer", label: "Angebot", icon: FileText, hideForInstaller: true },
  { id: "offers", label: "Angebote", icon: FileCheck, hideForInstaller: true, withCount: true },
  { id: "tech", label: "Technik", icon: ClipboardCheck },
  { id: "media", label: "Medien", icon: ImageIcon },
  { id: "status", label: "Status", icon: GitBranch },
  { id: "docs", label: "Dokumente", icon: Layers, hideForInstaller: true },
  { id: "tasks", label: "Aufgaben", icon: ListTodo },
  { id: "notes", label: "Bemerkungen", icon: MessageSquare },
  { id: "history", label: "Chronik", icon: HistoryIcon },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isInstaller = user?.role === "installer";
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const canHandover = ["sales_manager","sales_director","admin","super_admin"].includes(user?.role) || user?.is_super_admin;

  // --- Handover Modal (Innendienst → Außendienst) ---
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [handoverTargets, setHandoverTargets] = useState([]);
  const [handoverTargetId, setHandoverTargetId] = useState("");
  const [handoverNote, setHandoverNote] = useState("");
  const [handoverSaving, setHandoverSaving] = useState(false);
  const [handoverLoading, setHandoverLoading] = useState(false);
  const openHandover = async () => {
    setHandoverOpen(true);
    setHandoverLoading(true);
    try {
      const r = await api.get("/users", { params: { roles: "sales_rep,sales_manager,sales_director" } });
      const list = Array.isArray(r.data) ? r.data : (r.data.users || []);
      setHandoverTargets(list.filter(u => u.active !== false && u.status !== "deleted"));
    } catch { setHandoverTargets([]); }
    finally { setHandoverLoading(false); }
  };
  const submitHandover = async () => {
    if (!handoverTargetId) { toast.error("Bitte Außendienstler auswählen"); return; }
    setHandoverSaving(true);
    try {
      const r = await api.post(`/projects/${id}/handover`, {
        assign_to_user_id: handoverTargetId,
        note: handoverNote || undefined,
      });
      toast.success(`Übergeben an ${r.data.assigned_to_name}`);
      setHandoverOpen(false);
      setHandoverTargetId(""); setHandoverNote("");
      // Projekt neu laden — verwendet den existierenden `reload()`-Handler
      if (typeof reload === "function") await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übergabe fehlgeschlagen");
    } finally { setHandoverSaving(false); }
  };
  const TABS = ALL_TABS.filter((t) => !(isInstaller && t.hideForInstaller));
  const [searchParams, setSearchParams] = useSearchParams();
  // Per-user "last viewed tab" memory so that returning to a project lands
  // on the section the user was working on last. Order of precedence:
  //   1. explicit ?tab= URL param (deep link / push notification)
  //   2. last tab the user actively clicked (localStorage, scoped per user)
  //   3. role default (installer → tech, otherwise → customer)
  const tabMemoryKey = `novatus_last_tab_${user?.id || "anon"}`;
  const urlTab = TABS.find((t) => t.id === searchParams.get("tab"))?.id;
  let storedTab = null;
  try {
    const raw = localStorage.getItem(tabMemoryKey);
    if (raw && TABS.find((t) => t.id === raw)) storedTab = raw;
  } catch { /* localStorage disabled / private mode */ }
  const initialTab = urlTab || storedTab || (isInstaller ? "tech" : "customer");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Handy: aktiven Tab in der Leiste zentrieren + Inhalt unter die Tab-Leiste scrollen
  const focusTab = (btnEl) => {
    try {
      btnEl?.scrollIntoView?.({ inline: "center", block: "nearest", behavior: "smooth" });
      if (window.innerWidth < 1280) {
        requestAnimationFrame(() => {
          const el = document.querySelector('[data-testid="project-tab-content"]');
          if (!el) return;
          const top = el.getBoundingClientRect().top + window.scrollY - (window.innerWidth < 1280 ? 64 : 125);
          if (window.scrollY < top) window.scrollTo({ top, behavior: "smooth" });
        });
      }
    } catch { /* noop */ }
  };

  // Persist the tab choice whenever the user actively switches.
  const handleTabChange = (tid, btnEl) => {
    setTab(tid);
    try { localStorage.setItem(tabMemoryKey, tid); } catch { /* noop */ }
    focusTab(btnEl);
    // Iter88zz3 — URL-Query mitziehen, damit Deeplinks (z. B. Segment-Klick
    // → Status-Tab) beim Reload/Teilen konsistent bleiben. Wenn `tab` gleich
    // dem Default ist, bleibt der Param leer.
    try {
      const next = new URLSearchParams(searchParams);
      if (tid === (TABS[0]?.id || "overview")) next.delete("tab"); else next.set("tab", tid);
      setSearchParams(next, { replace: true });
    } catch { /* noop */ }
    // Broadcast to the global HelpFab so it can show tab-specific content.
    try {
      window.dispatchEvent(new CustomEvent("novatus:project-tab-change", { detail: { tab: tid } }));
    } catch { /* noop */ }
  };

  // Broadcast initial tab on mount + clear on unmount, so HelpFab knows
  // the context whenever this page is active.
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent("novatus:project-tab-change", { detail: { tab } }));
    } catch { /* noop */ }
    return () => {
      try {
        window.dispatchEvent(new CustomEvent("novatus:project-tab-change", { detail: { tab: null } }));
      } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Sync local tab state when the URL param changes (e.g. after navigate()
  // from a child button like "Konfigurator" in OffersListSection which
  // switches ?tab=offers → ?tab=offer&edit=xxx). Without this, the tab
  // state stays on the initial value and clicks appear to do nothing.
  // Using searchParams.toString() (a primitive) as the dependency is more
  // reliable than the object itself — React's shallow compare on the
  // URLSearchParams instance is not guaranteed to change on same-route
  // param updates.
  const searchParamsKey = searchParams.toString();
  useEffect(() => {
    const nextTab = TABS.find((t) => t.id === searchParams.get("tab"))?.id;
    if (nextTab && nextTab !== tab) {
      setTab(nextTab);
      try { localStorage.setItem(tabMemoryKey, nextTab); } catch { /* noop */ }
      // Scroll to top so the user immediately sees the switched section.
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsKey]);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    api.get(`/projects/${id}`)
      .then((r) => {
        setData(r.data);
        setNotFound(false);
      })
      .catch((e) => {
        if (e?.response?.status === 404) {
          setNotFound(true);
          setData(null);
        } else if (!silent) {
          toast.error("Projekt konnte nicht geladen werden");
        }
      })
      .finally(() => { if (!silent) setLoading(false); });
  };

  // Silent reload – never unmounts the children, so camera / modal state is preserved.
  const reload = () => load(true);

  // Iter88zbt — Auto-Reload wenn IRGENDWO auf der Detail-Seite eine
  // Änderung gespeichert wurde (KfW, Verbrauch, Angebote, Bemerkungen, …).
  useReloadBus("project-data", reload);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);
  // Nach dem Laden: aktiven Tab sichtbar machen; bei Deeplink (?tab=…) direkt zum Inhalt
  useEffect(() => {
    if (loading || !data) return;
    const btn = document.querySelector(`[data-testid="project-tab-${tab}"]`);
    if (urlTab) focusTab(btn);
    else btn?.scrollIntoView?.({ inline: "center", block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);


  const updateStatus = async (newStatus) => {
    try {
      await api.patch(`/projects/${id}`, { status: newStatus });
      toast.success(`Status: ${newStatus}`);
      load();
    } catch {
      toast.error("Statusänderung fehlgeschlagen");
    }
  };

  const deleteProject = async () => {
    const projectNo = data?.project?.project_number || id;
    const ok = window.confirm(
      `Projekt ${projectNo} wirklich endgültig löschen?\n\n` +
      `Inklusive aller verknüpften Angebote, Rechnungen, Dateien, Tracker und History.\n\n` +
      "⚠️ Vorgang ist NICHT reversibel — nur für Test-Daten gedacht.",
    );
    if (!ok) return;
    try {
      const r = await api.delete(`/projects/${id}`, { params: { cascade: true } });
      const counts = Object.entries(r.data?.deleted || {})
        .map(([k, v]) => `${v} ${k}`)
        .join(", ");
      toast.success(`Projekt ${projectNo} gelöscht`, { description: counts || "—" });
      navigate("/projects");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <AppHeader back="/projects" />
        <div className="flex items-center justify-center py-32 text-zinc-400">
          <Loader2 className="animate-spin mr-2" /> Lade Projekt…
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <AppHeader back="/projects" />
        <div className="max-w-md mx-auto px-6 py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-5">
            <FileText className="text-amber-400" size={28} />
          </div>
          <h1 className="font-display text-2xl font-bold text-zinc-100 mb-2">Projekt nicht gefunden</h1>
          <p className="text-sm text-zinc-400 leading-relaxed mb-6">
            Das angeforderte Projekt existiert nicht (mehr) oder Sie haben keine Berechtigung,
            es einzusehen. Es kann durch einen Admin-Reset oder einen Schulungs-Modus-Wechsel
            entfernt worden sein.
          </p>
          <Link
            to="/projects"
            data-testid="project-notfound-back"
            className="inline-flex items-center justify-center h-11 px-6 rounded-xl bg-[#00E575] text-[#09090B] font-semibold hover:bg-[#00C964] active:scale-95 transition-all"
          >
            <ChevronRight size={16} className="rotate-180 mr-1" /> Zurück zur Projektliste
          </Link>
        </div>
      </div>
    );
  }

  const { project, customer, consumption, technical_check, photos, videos, sketches, offers, history } = data;

  // Iter88zz2 — Fortschrittsleiste zeigt jetzt echten Projekt-Fortschritt
  // (tracker.current_stage aus dem Backend, project.stage_progress).
  // Fallback bleibt die alte tech_check-Kompletion, falls das Backend
  // die neuen Felder (noch) nicht mitliefert.
  const stageProg = project?.stage_progress;
  const techFields = technical_check ? Object.keys(technical_check).filter((k) => !["id","project_id","created_at","updated_at","photo_consent_given"].includes(k)) : [];
  const filled = techFields.filter((k) => technical_check[k] !== null && technical_check[k] !== "" && technical_check[k] !== undefined).length;
  const techProgressFallback = techFields.length ? Math.round((filled / techFields.length) * 100) : 0;
  const progressPct = stageProg?.progress_pct ?? techProgressFallback;
  const progressLabel = stageProg?.current_label
    ? `${stageProg.current_label} · ${stageProg.completed_mandatory}/${stageProg.total_mandatory}`
    : "Technische Aufnahme";

  // Iter88zz3 — Segmentierter Fortschrittsbalken (User-Wunsch).
  // Jede Pflicht-Stufe = ein klickbares Mini-Segment; optionale Stufen
  // (`orientation_offer_sent`) erscheinen als schmales Zwischen-Pill über
  // dem Balken, damit sie sichtbar sind, ohne den 15er-Rhythmus zu stören.
  // Ein Klick springt zum Status-Tab, damit man die Detail-Timeline
  // direkt öffnen kann.
  const trackerStages = project?.tracker?.stages || [];
  const stageCompletionByKey = Object.fromEntries(
    trackerStages.filter((s) => s.completed).map((s) => [s.key, s])
  );
  const currentStageKey = stageProg?.current_stage;
  const stageOrder = stageProg?.stage_order || [];
  const mandatorySegments = stageOrder.filter((s) => !s.optional);
  const optionalSegments = stageOrder.filter((s) => s.optional);
  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader back="/projects" title="" />

      {/* Project hero */}
      <div className="border-b border-zinc-800 bg-[#0E0E11]">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <p className="font-mono text-[12px] text-[#00E575] tracking-[0.2em] uppercase font-medium">{project.project_number}</p>
                {isDemoProject(project) && <TrainingBadge size="lg" />}
                {project.stage === "intake" && (
                  <span
                    data-testid="project-stage-intake"
                    className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-200 text-[12px] font-semibold uppercase tracking-wider"
                    title="Projekt befindet sich noch im Innendienst — Kundendaten, Verbrauch, Orientierungsangebot werden erfasst"
                  >
                    Innendienst
                  </span>
                )}
                {project.stage === "sales" && (
                  <span
                    data-testid="project-stage-sales"
                    className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-[12px] font-semibold uppercase tracking-wider"
                  >
                    Außendienst
                  </span>
                )}
              </div>
              <h1 className="font-display text-2xl md:text-3xl font-bold truncate">{customer?.first_name} {customer?.last_name}</h1>
              <p className="text-zinc-400 text-sm mt-1">{customer?.street} {customer?.house_number}, {customer?.zip} {customer?.city}</p>
              {project.offer_number && !isInstaller && <p className="text-[#00E575] font-mono text-sm mt-1">Angebots-Nr. {project.offer_number}</p>}
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              {!isInstaller && (
                <Link
                  to={`/projects/${id}/pv`}
                  data-testid="open-pv-configurator"
                  className="h-11 px-4 rounded-xl bg-gradient-to-br from-[#00E575] to-[#00C964] text-[#09090B] font-bold inline-flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all"
                  title="PV-Konfigurator: KI-Dachanalyse, Angebot, Speicher-Optimierer, Digital-Checkout"
                >
                  <Sun size={18} /> PV-Modul
                </Link>
              )}
              <div>
                <label className="block text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-widest">Status</label>
                <select
                  data-testid="project-status-select"
                  value={project.status}
                  onChange={(e) => updateStatus(e.target.value)}
                  className="h-11 bg-[#18181B] border border-zinc-700 rounded-xl px-4 pr-8 text-zinc-50 focus:border-[#00E575] outline-none min-w-[200px]"
                >
                  {(STATUS_OPTIONS.includes(project.status) || !project.status ? STATUS_OPTIONS : [project.status, ...STATUS_OPTIONS])
                    .map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {canHandover && project.stage === "intake" && (
                <button
                  type="button"
                  data-testid="project-handover-btn"
                  onClick={openHandover}
                  title="Innendienst-Projekt an Außendienstler übergeben (Vertrieb übernimmt technische Beratung + finales Angebot)"
                  className="h-11 px-4 rounded-xl bg-gradient-to-br from-amber-400 to-orange-400 text-[#09090B] font-bold inline-flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all self-end"
                >
                  <UserCheck size={16} /> Übergeben an Sales
                </button>
              )}
              {isAdmin && (
                <button
                  type="button"
                  data-testid="project-delete-btn"
                  onClick={deleteProject}
                  title="Projekt + Angebote + Rechnungen + History endgültig löschen"
                  className="h-11 px-3 rounded-xl bg-rose-500/10 border border-rose-500/40 text-rose-300 hover:bg-rose-500/20 inline-flex items-center gap-1.5 text-[12.5px] font-bold transition self-end"
                >
                  <Trash2 size={14} /> Löschen
                </button>
              )}
            </div>
          </div>

          {/* Progress — Iter88zz3: segmentierter Balken.
              Jedes Mini-Segment = eine Pflicht-Stage. Optionale Stufen
              erscheinen als schmales gelbes Pill über dem Balken. */}
          <div className="mt-5" data-testid="project-progress-bar">
            <div className="flex items-center justify-between text-xs text-zinc-400 mb-2">
              <span data-testid="project-progress-label">{progressLabel}</span>
              <span className="font-mono">{progressPct}%</span>
            </div>
            {mandatorySegments.length > 0 ? (
              <div className="space-y-1.5">
                {/* Optionale Zwischenstufen als kleine Pills — nur wenn abgehakt oder gerade aktiv */}
                {optionalSegments.some((s) => stageCompletionByKey[s.key] || s.key === currentStageKey) && (
                  <div className="flex flex-wrap gap-1.5" data-testid="progress-optional-row">
                    {optionalSegments.map((seg) => {
                      const done = !!stageCompletionByKey[seg.key];
                      const isCurrent = seg.key === currentStageKey;
                      if (!done && !isCurrent) return null;
                      return (
                        <button
                          key={seg.key}
                          type="button"
                          data-testid={`progress-optional-${seg.key}`}
                          onClick={() => handleTabChange("status")}
                          title={`${seg.label}${done ? ` · abgehakt ${fmtDate(stageCompletionByKey[seg.key]?.completed_at)}` : " · aktuell"}`}
                          className={`h-4 px-2 rounded-full text-[9px] font-bold uppercase tracking-wider inline-flex items-center gap-1 transition ${
                            done
                              ? "bg-amber-400/20 text-amber-300 border border-amber-400/40 hover:bg-amber-400/30"
                              : "bg-amber-400/10 text-amber-200 border border-amber-400/60 animate-pulse"
                          }`}
                        >
                          <span className="w-1 h-1 rounded-full bg-current" /> {seg.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {/* Pflicht-Segmente — 8-Pixel-Hitbox um jedes 2px-Segment,
                    damit man sie auch auf Touch-Geräten sicher trifft. */}
                <div className="flex gap-[3px]" data-testid="progress-segments">
                  {mandatorySegments.map((seg) => {
                    const completedAt = stageCompletionByKey[seg.key]?.completed_at;
                    const done = !!stageCompletionByKey[seg.key];
                    const isCurrent = seg.key === currentStageKey;
                    let cls = "bg-zinc-800 group-hover:bg-zinc-700";
                    if (done && isCurrent) cls = "bg-[#00E575] shadow-[0_0_10px_rgba(0,229,117,0.55)] animate-pulse";
                    else if (done) cls = "bg-[#00E575] group-hover:bg-[#00C964]";
                    else if (isCurrent) cls = "bg-[#00E575]/40 border border-[#00E575] animate-pulse";
                    return (
                      <button
                        key={seg.key}
                        type="button"
                        data-testid={`progress-seg-${seg.key}`}
                        onClick={() => handleTabChange("status")}
                        title={`${seg.label}${done ? ` · abgehakt ${fmtDate(completedAt)}` : isCurrent ? " · aktuelle Stufe" : ""}`}
                        aria-label={seg.label}
                        className="group flex-1 py-2 -my-2 cursor-pointer bg-transparent border-0 p-0"
                      >
                        <div className={`h-2 w-full rounded-full transition-all duration-500 ${cls}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              // Fallback (kein stage_progress) — alter Ein-Balken
              <div className="h-2 w-full bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-[#00E575] transition-all duration-500" style={{ width: `${progressPct}%` }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab nav (horizontal scroll)
          Iter88zbp — Overflow-Hinweis: rechts ein Fade-Gradient, damit
          User sofort sieht, dass es weitere Tabs zum Scrollen gibt. */}
      <div className="sticky top-0 xl:top-16 z-30 bg-[#09090B]/90 backdrop-blur-xl border-b border-zinc-800">
        <div className="relative max-w-6xl mx-auto">
          <div className="px-2 overflow-x-auto no-scrollbar">
            <div className="flex gap-0.5 py-2 min-w-max">
              {TABS.map(({ id: tid, label, icon: Icon, withCount }) => {
                const isActive = tab === tid;
                const count = withCount && tid === "offers" ? (offers?.length || 0) : null;
                return (
                  <button
                    key={tid}
                    data-testid={`project-tab-${tid}`}
                    onClick={(e) => handleTabChange(tid, e.currentTarget)}
                    className={`flex items-center gap-1.5 px-3 h-11 rounded-xl text-sm font-medium transition active:scale-95 ${
                      isActive
                        ? "bg-[#00E575] text-[#09090B]"
                        : "text-zinc-300 hover:bg-zinc-800/60"
                    }`}
                  >
                    <Icon size={16} /> {label}
                    {count != null && count > 0 && (
                      <span className={`ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[12px] font-extrabold ${
                        isActive ? "bg-[#09090B]/15 text-[#09090B]" : "bg-[#00E575]/20 text-[#00E575]"
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Fade-Overlay rechts — nur sichtbar wenn scrollbar */}
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#09090B] via-[#09090B]/70 to-transparent" aria-hidden />
        </div>
      </div>

      <main data-testid="project-tab-content" className="max-w-6xl mx-auto px-4 py-6 pb-24 min-h-screen">
        {/* Iter88 — Immer sichtbarer Kasten für unterschriebene Dokumente.
            Steht auf JEDEM Tab: signiertes Angebots-PDF + Liefer-/Leistungs-
            vertrag mit einem Klick abrufbar, damit niemand mehr in Sub-Tabs
            danach suchen muss. Rendert nichts, wenn kein signiertes Angebot
            existiert (siehe Komponente). */}
        <SignedDocumentsBanner offers={offers} onUpdate={reload} currentUser={user} />
        {/* Iter88zk (2026-02-19) — BzA-Panel gelöscht. Die Post-Signatur-
            Prozesskette ist in den „Projekt-Status"-Tracker unten
            zusammengeführt (15 vereinheitlichte Stufen mit Reopen-Möglichkeit
            und E-Mail-Resend). Kein doppeltes Rendern mehr. */}
        <TelesalesDataCheck
          project={project}
          customer={customer}
          consumption={consumption}
          onRefresh={reload}
        />
        {tab === "messages" && <MessageThread scopeType="project" scopeId={project.id} title={`Nachrichten zu ${project.project_number || "diesem Projekt"}`} />}
        {tab === "tasks" && <ProjectTasksSection project={project} />}
        {tab === "customer" && <CustomerSection project={project} customer={customer} onUpdate={reload} />}
        {tab === "consumption" && <ConsumptionSection project={project} consumption={consumption} onUpdate={reload} />}
        {tab === "tech" && <TechnicalCheckTabs project={project} tech={technical_check} photos={photos} onUpdate={reload} />}
        {tab === "media" && <MediaSection project={project} photos={photos} videos={videos} sketches={sketches} onUpdate={reload} />}
        {tab === "offer" && <div className="ios-no-zoom"><OfferSection project={project} customer={customer} consumption={consumption} offers={offers} onUpdate={reload} /></div>}
        {tab === "offers" && <div className="ios-no-zoom"><OffersListSection project={project} customer={customer} offers={offers} onUpdate={reload} /></div>}
        {tab === "status" && (
          <>
            {isAdmin && <StageOverrideButton project={project} onDone={reload} />}
            <ProjectStatusTracker project={project} customer={customer} offers={offers} onUpdate={reload} />
            <div className="mt-4"><SentDocumentsPanel projectId={project.id} collapsible reloadKey={project.updated_at} /></div>
          </>
        )}
        {tab === "docs" && <ProjectDocumentsCockpit projectId={project.id} />}
        {tab === "notes" && <NotesSection project={project} />}
        {tab === "history" && <HistorySection history={history} />}
      </main>

      {/* Handover Modal (Innendienst → Außendienst) */}
      {handoverOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="handover-modal">
          <div className="bg-[#18181B] border border-amber-500/30 rounded-2xl w-full max-w-md overflow-hidden">
            <div className="border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck size={16} className="text-amber-300" />
                <h3 className="font-display text-lg font-semibold text-amber-100">Übergabe an Außendienst</h3>
              </div>
              <button
                onClick={() => setHandoverOpen(false)}
                disabled={handoverSaving}
                data-testid="handover-close"
                className="text-zinc-400 hover:text-zinc-100 disabled:opacity-40"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="text-[12px] text-zinc-300 font-medium leading-relaxed">
                Projekt <span className="text-[#00E575] font-mono">{project.project_number}</span> wird an einen Sales-Rep übergeben.
                Alle bisher erfassten Daten (Kunde, Verbrauch, KfW-Rechnung, Orientierungsangebot) bleiben erhalten.
              </div>
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-wider text-zinc-200 mb-1">Zuweisen an</label>
                {handoverLoading ? (
                  <div className="h-11 flex items-center justify-center bg-[#0F0F11] border border-zinc-800 rounded-lg text-[12px] text-zinc-400"><Loader2 className="animate-spin mr-2" size={12} /> Lade Team…</div>
                ) : (
                  <select
                    data-testid="handover-target-select"
                    value={handoverTargetId}
                    onChange={(e) => setHandoverTargetId(e.target.value)}
                    className="h-11 w-full bg-[#0F0F11] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-100 focus:border-amber-400 outline-none"
                  >
                    <option value="">– bitte wählen –</option>
                    {handoverTargets.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} · {u.role === "sales_rep" ? "Sales-Agent" : u.role === "sales_manager" ? "Vertriebsleiter" : u.role === "branch_manager" ? "Niederlassungsleiter" : "Vertriebsdirektor"}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-wider text-zinc-200 mb-1">Notiz (optional)</label>
                <textarea
                  data-testid="handover-note"
                  value={handoverNote}
                  onChange={(e) => setHandoverNote(e.target.value)}
                  rows={2}
                  placeholder='z. B. "Kunde interessiert an 12 kWp PV plus WP" oder "Termin für Vor-Ort schon vereinbart"'
                  className="w-full bg-[#0F0F11] border border-zinc-700 rounded-lg px-3 py-2 text-[13px] text-zinc-100 focus:border-amber-400 outline-none resize-none"
                />
              </div>
            </div>
            <div className="border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
              <button
                onClick={() => setHandoverOpen(false)}
                disabled={handoverSaving}
                className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium text-zinc-200 disabled:opacity-40"
              >
                Abbrechen
              </button>
              <button
                onClick={submitHandover}
                disabled={handoverSaving || !handoverTargetId}
                data-testid="handover-confirm"
                className="h-9 px-4 rounded-lg bg-gradient-to-br from-amber-400 to-orange-400 text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                {handoverSaving ? <Loader2 className="animate-spin" size={12} /> : <UserCheck size={13} />}
                Übergeben
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
